import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Power, Check, X, Building } from 'lucide-react';
import { toast } from 'sonner';
import { api, inputBase } from './api';

const InlineForm = ({ initial, onSave, onCancel, withCompany, companies, testPrefix }) => {
  const [form, setForm] = useState(initial);
  return (
    <div className="flex flex-wrap items-center gap-2 p-2 bg-slate-50 rounded-lg" data-testid={`${testPrefix}-form`}>
      <input autoFocus placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={`${inputBase} flex-1 min-w-[160px]`} data-testid={`${testPrefix}-name-input`} />
      <input placeholder="Code" value={form.code || ''} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} className={`${inputBase} w-24`} data-testid={`${testPrefix}-code-input`} />
      {withCompany && (
        <select value={form.company_id} onChange={e => setForm({ ...form, company_id: e.target.value })} className={`${inputBase} w-48`} data-testid={`${testPrefix}-company-select`}>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}
      <button onClick={() => onSave(form)} className="p-2 text-white bg-slate-900 rounded-lg" data-testid={`${testPrefix}-save-btn`}><Check className="w-4 h-4" /></button>
      <button onClick={onCancel} className="p-2 text-slate-500 bg-white border border-slate-200 rounded-lg" data-testid={`${testPrefix}-cancel-btn`}><X className="w-4 h-4" /></button>
    </div>
  );
};

const Row = ({ item, label, onEdit, onToggle, onDelete, testId }) => (
  <div className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 ${item.active ? '' : 'opacity-50'}`} data-testid={testId}>
    <div className="min-w-0">
      <span className="font-medium text-slate-800 text-sm">{item.name}</span>
      {item.code && <span className="ml-2 px-1.5 py-0.5 text-[10px] font-mono bg-slate-100 rounded">{item.code}</span>}
      {!item.active && <span className="ml-2 text-xs text-amber-700">disabled</span>}
      {label}
    </div>
    <div className="flex gap-1 shrink-0">
      <button onClick={onToggle} title={item.active ? 'Disable' : 'Enable'} className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded" data-testid={`${testId}-toggle`}><Power className="w-4 h-4" /></button>
      <button onClick={onEdit} title="Edit" className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded" data-testid={`${testId}-edit`}><Edit2 className="w-4 h-4" /></button>
      <button onClick={onDelete} title="Delete" className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`${testId}-delete`}><Trash2 className="w-4 h-4" /></button>
    </div>
  </div>
);

export const OrgMasters = ({ companies, bus, reload }) => {
  const [editing, setEditing] = useState(null); // {kind, item}

  const save = async (kind, form) => {
    const path = kind === 'company' ? '/companies' : '/business-units';
    try {
      await api(form.id ? `${path}/${form.id}` : path, { method: form.id ? 'PUT' : 'POST', body: JSON.stringify(form) });
      toast.success('Saved');
      setEditing(null);
      reload();
    } catch (err) { toast.error(err.message); }
  };
  const toggle = (kind, item) => save(kind, { ...item, active: !item.active });
  const remove = async (kind, item) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    try { await api(`${kind === 'company' ? '/companies' : '/business-units'}/${item.id}`, { method: 'DELETE' }); toast.success('Deleted'); reload(); } catch (err) { toast.error(err.message); }
  };
  const isEditing = (kind, id) => editing?.kind === kind && editing.item.id === id;

  return (
    <div className="space-y-4" data-testid="org-masters">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Companies in the group and the business units under each. Disable to hide temporarily; delete only when unused.</p>
        <button onClick={() => setEditing({ kind: 'company', item: { name: '', code: '', active: true } })} data-testid="add-company-btn" className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-900 text-white rounded-lg"><Plus className="w-4 h-4" /> Company</button>
      </div>
      {editing?.kind === 'company' && !editing.item.id && <InlineForm testPrefix="company" initial={editing.item} onSave={f => save('company', f)} onCancel={() => setEditing(null)} />}
      {companies.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No companies yet</p>}
      {companies.map(c => (
        <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`company-card-${c.id}`}>
          {isEditing('company', c.id) ? <InlineForm testPrefix="company" initial={editing.item} onSave={f => save('company', f)} onCancel={() => setEditing(null)} /> : (
            <Row item={c} testId={`company-row-${c.id}`} label={<Building className="inline w-4 h-4 ml-2 text-slate-300" />}
              onEdit={() => setEditing({ kind: 'company', item: c })} onToggle={() => toggle('company', c)} onDelete={() => remove('company', c)} />
          )}
          <div className="ml-4 mt-2 border-l-2 border-slate-100 pl-3 space-y-1">
            {bus.filter(b => b.company_id === c.id).map(b => isEditing('bu', b.id) ? (
              <InlineForm key={b.id} testPrefix="bu" withCompany companies={companies} initial={editing.item} onSave={f => save('bu', f)} onCancel={() => setEditing(null)} />
            ) : (
              <Row key={b.id} item={b} testId={`bu-row-${b.id}`} onEdit={() => setEditing({ kind: 'bu', item: b })} onToggle={() => toggle('bu', b)} onDelete={() => remove('bu', b)} />
            ))}
            {editing?.kind === 'bu' && !editing.item.id && editing.item.company_id === c.id ? (
              <InlineForm testPrefix="bu" withCompany companies={companies} initial={editing.item} onSave={f => save('bu', f)} onCancel={() => setEditing(null)} />
            ) : (
              <button onClick={() => setEditing({ kind: 'bu', item: { name: '', code: '', company_id: c.id, active: true } })} data-testid={`add-bu-btn-${c.id}`} className="flex items-center gap-1 px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg"><Plus className="w-3.5 h-3.5" /> Business unit</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
